import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserProfile } from '@backstage/plugin-user-settings';
import {
  MaterialIcon,
  useTheme,
  IconButton,
  Avatar,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemButton,
  styledComponent,
  ListItemText,
  ListItemAvatar,
  Typography,
  ListItemButtonProps,
  PaperProps,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';
import LogoLight from '../../../assets/navigation/logo-light.svg';
import LogoDark from '../../../assets/navigation/logo-dark.svg';
import useAsync from 'react-use/esm/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { useUserEntity } from '@dewise-devex/backstage-plugin-utilities-common-client';

const Container = styledComponent<PaperProps>(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
}));

const ItemButton = styledComponent<ListItemButtonProps>(ListItemButton)(() => ({
  borderRadius: '100px',
}));

/**
 * A component that provides theme toggle and user settings navigation functionality.
 *
 * @category Navigation
 */
const Settings = (): React.ReactElement => {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, loading } = useUserProfile();
  const userEntityService = useUserEntity();
  const microsoftAuthApi = useApi(microsoftAuthApiRef);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setAnchorEl(null);
  }, [location.pathname]);

  const { value: entity, loading: entityLoading } = useAsync(async () => {
    if (profile?.email) {
      return await userEntityService.byEmail(profile.email);
    }

    return undefined;
  }, [profile?.email]);

  const id = open ? 'simple-popper' : undefined;

  /**
   * Navigates to the settings page.
   */
  const goToSettings = () => {
    navigate('/settings');
    setOpen(false);
  };

  const goToProfile = () => {
    if (entity) {
      navigate(
        `/catalog/${entity.metadata.namespace}/user/${entity.metadata.name}`,
      );
      setOpen(false);
    }
  };

  /**
   * Toggles the current theme between 'light' and 'dark'.
   */
  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');

  /**
   * Memoized icon component based on the current theme.
   * Switches between light mode and dark mode icons.
   */
  const ToggleIcon = React.useMemo(
    () => (theme === 'dark' ? MaterialIcon.light_mode : MaterialIcon.dark_mode),
    [theme],
  );

  const popperRef = React.useRef(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(prevOpen => !prevOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      popperRef.current &&
      !(popperRef.current as HTMLElement).contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  const handleLogout = () => {
    microsoftAuthApi
      .signOut()
      .then(() => {
        window.location.href = '/';
      })
      .catch(() => {
        throw new Error('Failed to logout');
      });
  };

  React.useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div
      style={{
        position: 'absolute',
        right: '50px',
        display: 'flex',
        columnGap: '16px',
      }}
    >
      <IconButton color="fill" onClick={toggleTheme}>
        <ToggleIcon fontSize="small" />
      </IconButton>
      {profile.picture && !loading && !entityLoading && (
        <>
          <IconButton
            color="fill"
            sx={{
              borderRadius: '22px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              px: 0.5,
            }}
            onClick={handleClick}
          >
            <img
              src={theme === 'light' ? LogoLight : LogoDark}
              alt="logo"
              style={{ width: 'auto', height: 12, marginLeft: 4 }}
            />
            <Avatar
              sx={{ width: 24, height: 24 }}
              src={profile.picture}
              alt={profile.displayName}
            />
          </IconButton>

          <Popper
            id={id}
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            sx={{ zIndex: 999 }}
            ref={popperRef}
          >
            <Container elevation={0}>
              <img src={theme === 'light' ? LogoLight : LogoDark} alt="logo" />
              <List>
                <ListItem disablePadding>
                  <ItemButton
                    onClick={goToProfile}
                    selected={location.pathname.includes('user')}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{ width: 40, height: 40 }}
                        src={profile.picture}
                        alt={profile.displayName}
                      />
                    </ListItemAvatar>
                    <ListItemText>
                      <Typography variant="body1">
                        {profile.displayName}
                      </Typography>
                      <Typography fontSize={12}>{profile.email}</Typography>
                    </ListItemText>
                  </ItemButton>
                </ListItem>
                <ListItem divider />
                <ListItem disablePadding sx={{ my: 1 }}>
                  <ItemButton
                    onClick={goToSettings}
                    selected={location.pathname.includes('settings')}
                  >
                    <ListItemText>
                      <Typography fontSize={14} fontWeight="medium">
                        Settings
                      </Typography>
                    </ListItemText>
                  </ItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mt: 1 }} disableGutters>
                  <ItemButton onClick={() => handleLogout()}>
                    <ListItemText>
                      <Typography fontSize={14} fontWeight="medium">
                        Logout
                      </Typography>
                    </ListItemText>
                  </ItemButton>
                </ListItem>
              </List>
            </Container>
          </Popper>
        </>
      )}
    </div>
  );
};

export default Settings;
